import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DocumentViewerDialog from "../../TravelERP/DocumentViewerDialog";
import {
  CameraCaptureDialog,
  normalizeImageFile,
} from "../../../utils/imageCapture";
import ImageSourceDialog from "./ImageSourceDialog";
import { PRIMARY } from "./shared";

// ─── NomineeDocuments sub-component (reusable inside the nominee dialog) ──────
const NomineeDocuments = ({ documents, onChange, nomineeMobile, prefix }) => {
  const [imageSourceDialog, setImageSourceDialog] = useState({
    open: false,
    index: null,
  });
  const [cameraIndex, setCameraIndex] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);

  const hasOther = documents.some((d) => d.name === "Other");

  const updateDoc = (i, field, value) => {
    const docs = [...documents];
    docs[i] = { ...docs[i], [field]: value };
    onChange(docs);
  };

  const setDocFile = (i, dataUrl, fileName) => {
    const docs = [...documents];
    docs[i] = { ...docs[i], file: dataUrl, fileName };
    onChange(docs);
  };

  const handleFileUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Bake in EXIF orientation so phone photos don't come out rotated.
    const dataUrl = await normalizeImageFile(file);
    setDocFile(i, dataUrl, file.name);
    e.target.value = "";
  };

  const handleAddOther = () => {
    if (hasOther) return;
    onChange([
      ...documents,
      { name: "Other", number: "", file: null, fileName: "", renewalDate: "" },
    ]);
  };

  const handleRemoveOther = () => {
    onChange(documents.filter((d) => d.name !== "Other"));
  };

  return (
    <Box>
      {/* Doc section header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: PRIMARY }}
        >
          Documents
        </Typography>
        {hasOther ? (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={handleRemoveOther}
            sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.7rem" }}
          >
            Remove Other
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddRoundedIcon />}
            onClick={handleAddOther}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontSize: "0.7rem",
              color: PRIMARY,
              borderColor: PRIMARY,
            }}
          >
            Add Other
          </Button>
        )}
      </Box>

      <Grid container spacing={1.5}>
        {documents.map((doc, i) => (
          <Grid item xs={12} sm={6} key={i}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: doc.file
                  ? `1px solid ${PRIMARY}40`
                  : "1px solid #e0e0e0",
                backgroundColor: doc.file ? `${PRIMARY}04` : "#fff",
              }}
            >
              {/* Doc header row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {doc.file ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: "#4caf50" }} />
                  ) : (
                    <ImageIcon sx={{ fontSize: 16, color: PRIMARY }} />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: PRIMARY }}
                  >
                    {doc.name}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {doc.name === "Other" && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        onChange(documents.filter((_, idx) => idx !== i));
                      }}
                      sx={{ p: 0.3 }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                  <Tooltip title="Replace image">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setImageSourceDialog({ open: true, index: i })
                      }
                      sx={{
                        color: PRIMARY,
                        backgroundColor: `${PRIMARY}10`,
                        p: 0.3,
                        "&:hover": { backgroundColor: `${PRIMARY}20` },
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Number field */}
              <TextField
                size="small"
                fullWidth
                label="Document Number"
                value={doc.number}
                onChange={(e) => updateDoc(i, "number", e.target.value)}
                placeholder={
                  doc.name === "Photo"
                    ? "Auto-filled from mobile"
                    : `Enter ${doc.name} number`
                }
                disabled={doc.name === "Photo"}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: doc.name === "Photo" ? "#f5f5f5" : "#fff",
                    fontSize: "0.8rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                }}
              />

              {/* File upload area */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  minHeight: 120,
                  borderRadius: 1.5,
                  border: `2px dashed ${doc.file ? PRIMARY : "#d0d0d0"}`,
                  backgroundColor: doc.file ? `${PRIMARY}08` : "#fafafa",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: PRIMARY,
                    backgroundColor: `${PRIMARY}04`,
                  },
                }}
                onClick={() => {
                  if (doc.file && doc.file !== "No file") {
                    let viewDoc = doc;
                    if (
                      typeof doc.file === "string" &&
                      doc.file.startsWith("data:") &&
                      (!doc.fileName || !doc.fileName.includes("."))
                    ) {
                      const mimeMatch = doc.file.match(
                        /^data:([a-zA-Z]+\/([a-zA-Z0-9+\-_.]+));base64,/,
                      );
                      const ext = mimeMatch ? mimeMatch[2] : "jpg";
                      viewDoc = {
                        ...doc,
                        fileName: `${doc.name || "document"}.${ext}`,
                      };
                    }
                    setViewerDoc(viewDoc);
                  } else {
                    setImageSourceDialog({ open: true, index: i });
                  }
                }}
              >
                {doc.file && doc.file !== "No file" ? (
                  <Box
                    component="img"
                    src={doc.file}
                    alt={`${doc.name} preview`}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <>
                    <CloudUploadIcon
                      sx={{ fontSize: 28, color: PRIMARY, opacity: 0.8 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        px: 1,
                        mt: 0.5,
                        fontSize: "0.65rem",
                      }}
                    >
                      Tap to upload
                    </Typography>
                  </>
                )}
              </Box>

              {/* Hidden file inputs */}
              <input
                id={`${prefix}camera-input-${i}`}
                type="file"
                hidden
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileUpload(i, e)}
              />
              <input
                id={`${prefix}gallery-input-${i}`}
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleFileUpload(i, e)}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <ImageSourceDialog
        open={imageSourceDialog.open}
        index={imageSourceDialog.index}
        prefix={prefix}
        onClose={() => setImageSourceDialog({ open: false, index: null })}
        onTakePhoto={() => setCameraIndex(imageSourceDialog.index)}
      />

      <CameraCaptureDialog
        open={cameraIndex !== null}
        onClose={() => setCameraIndex(null)}
        onCapture={(dataUrl) => {
          if (cameraIndex !== null)
            setDocFile(cameraIndex, dataUrl, "camera-photo.jpg");
        }}
      />

      <DocumentViewerDialog
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
      />
    </Box>
  );
};

export default NomineeDocuments;
